import React from 'react';
import { Grid, TextField } from '@ha/dsl';
import CountryCodeSelector from './CountryCodeSelector';
import { PhoneNumber, Country } from './types';

export interface Props {
  value?: PhoneNumber;
  id: string;
  className?: string;
  onChange?: (phoneNumber: PhoneNumber) => void;
  onFocus?: (phoneNumber: PhoneNumber) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  inputLabel?: string;
  showCountryCode?: boolean;
  selectPlaceholder?: string;
  countries: Country[];
  error?: string;
  fullWidth?: boolean;
  sameSizeFields?: boolean;
}

const PhoneInput: React.FC<Props> = ({
  value = { code: '', phone: '' },
  id,
  inputLabel = '',
  className = '',
  disabled = false,
  onChange,
  onFocus,
  onBlur,
  countries,
  selectPlaceholder = '',
  fullWidth = false,
  sameSizeFields = false,
}) => {
  const handlePhoneChange = React.useCallback(
    event => {
      if (onChange) {
        onChange({
          code: value.code,
          phone: String(event.target.value.replace(/\D/g, '')),
        });
      }
    },
    [value],
  );

  const handleCodeChange = React.useCallback(
    (_, values) => {
      if (onChange) {
        onChange({
          code: values ? values.code : '',
          phone: value.phone,
        });
      }
    },
    [value],
  );

  const handleFocus = React.useCallback(() => {
    if (onFocus) {
      onFocus({
        code: value.code,
        phone: value.phone,
      });
    }
  }, [value]);

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid item xs={6} sm={sameSizeFields ? 6 : 4}>
        <CountryCodeSelector
          countryCode={value.code}
          onCodeChange={handleCodeChange}
          countries={countries}
          selectPlaceholder={selectPlaceholder}
          inputProps={{ 'data-test-locator': 'Phone Input Prefix' }}
        />
      </Grid>
      <Grid item xs={6} sm={sameSizeFields ? 6 : 8}>
        <TextField
          type="tel"
          inputProps={{
            onBlur,
            'data-test-locator': 'Phone Number Input',
          }}
          autoComplete="tel"
          id={`${id}-phone`}
          label={inputLabel}
          className={className}
          onChange={handlePhoneChange}
          onFocus={handleFocus}
          value={value.phone}
          fullWidth={fullWidth}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

export default PhoneInput;
